// PrelevementSolutionResineAmorceur.jsx
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../api";
import { Scanner } from "@yudiel/react-qr-scanner";
import { DEFAULT_PRELEVEMENT_ENTITE, PRELEVEMENT_ENTITES } from "./prelevementEntites";

import { useI18n } from "../i18n/I18nContext";

function normalize(text) {
  return text.toString().toLowerCase().normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, " ").trim();
}
function toFloat(v) {
  if (!v && v !== 0) return 0;
  return Number(v.toString().replace(",", ".")) || 0;
}
function isAndroid() { return /Android/i.test(navigator.userAgent || ""); }

export default function PrelevementSolutionResineAmorceur() {
  const { t, ui } = useI18n();

  const navigate = useNavigate();

  const [operator, setOperator]                     = useState("");
  const [entite, setEntite]                         = useState(DEFAULT_PRELEVEMENT_ENTITE);
  const [pnResine, setPnResine]                     = useState("");
  const [pnResineSearch, setPnResineSearch]         = useState("");
  const [resinePns, setResinePns]                   = useState([]);
  const [resineStock, setResineStock]               = useState({});
  const [showResineList, setShowResineList]         = useState(false);
  const [showResineScanner, setShowResineScanner]   = useState(false);
  const [pnAmorceur, setPnAmorceur]                 = useState("");
  const [pnAmorceurSearch, setPnAmorceurSearch]     = useState("");
  const [amorceurPns, setAmorceurPns]               = useState([]);
  const [amorceurStock, setAmorceurStock]           = useState({});
  const [showAmorceurList, setShowAmorceurList]     = useState(false);
  const [showAmorceurScanner, setShowAmorceurScanner] = useState(false);
  const [qtyResineKg, setQtyResineKg]               = useState("");
  const [qtyAmorceurKg, setQtyAmorceurKg]           = useState("");
  const [qtyMelangeFinal, setQtyMelangeFinal]       = useState("");
  const [temperature, setTemperature]               = useState("");
  const [viscosite, setViscosite]                   = useState("");
  const [dureeRepos, setDureeRepos]                 = useState("");
  const [solutionParcNumber, setSolutionParcNumber] = useState("");
  const [commentSolution, setCommentSolution]       = useState("");
  const [now, setNow]                               = useState(new Date());
  const [sending, setSending]                       = useState(false);
  const [printing, setPrinting]                     = useState(false);
  const [error, setError]                           = useState("");
  const [success, setSuccess]                       = useState("");
  const resineBoxRef   = useRef(null);
  const amorceurBoxRef = useRef(null);

  useEffect(() => {
    const raw = localStorage.getItem("user");
    if (raw) { try { setOperator(JSON.parse(raw).username || ""); } catch (e) { console.error(e); } }
  }, []);

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    function onOutside(e) {
      if (resineBoxRef.current   && !resineBoxRef.current.contains(e.target))   setShowResineList(false);
      if (amorceurBoxRef.current && !amorceurBoxRef.current.contains(e.target)) setShowAmorceurList(false);
    }
    document.addEventListener("mousedown", onOutside);
    document.addEventListener("touchstart", onOutside);
    return () => { document.removeEventListener("mousedown", onOutside); document.removeEventListener("touchstart", onOutside); };
  }, []);

  const loadResineStock = useCallback(async () => {
    try {
      const res = await apiFetch("/api/resine/list");
      if (!res.ok) return;
      const data = await res.json();
      if (!Array.isArray(data)) return;
      const stock = {};
      const pns = [];
      data.forEach((item) => {
        const pn = item.PN || item.pn;
        if (!pn) return;
        pns.push(pn);
        const masse = toFloat(item.Masse);
        const restante = item.MasseRestante !== "" && item.MasseRestante != null
          ? toFloat(item.MasseRestante)
          : masse;
        stock[pn] = { masse, masseRestante: restante };
      });
      setResinePns(Array.from(new Set(pns)));
      setResineStock(stock);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    loadResineStock();
  }, [loadResineStock]);

  useEffect(() => {
    if (!pnResine) return;
    loadResineStock();
  }, [pnResine, loadResineStock]);

  const loadAmorceurStock = useCallback(async () => {
    try {
      const res = await apiFetch("/api/amorceur/list");
      if (!res.ok) return;
      const data = await res.json();
      if (!Array.isArray(data)) return;
      const stock = {};
      const pns = [];
      data.forEach((item) => {
        const pn = item.PN || item.pn;
        if (!pn) return;
        pns.push(pn);
        const conditionnementInitial = item.ConditionnementInitial !== "" && item.ConditionnementInitial != null
          ? toFloat(item.ConditionnementInitial)
          : toFloat(item.Conditionnement);
        const conditionnementRestant = item.Conditionnement !== "" && item.Conditionnement != null
          ? toFloat(item.Conditionnement)
          : conditionnementInitial;
        stock[pn] = { conditionnementInitial, conditionnementRestant };
      });
      setAmorceurPns(Array.from(new Set(pns)));
      setAmorceurStock(stock);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    loadAmorceurStock();
  }, [loadAmorceurStock]);

  useEffect(() => {
    if (!pnAmorceur) return;
    loadAmorceurStock();
  }, [pnAmorceur, loadAmorceurStock]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        loadResineStock();
        loadAmorceurStock();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [loadAmorceurStock, loadResineStock]);

  const filteredResine   = resinePns.filter((p)   => { const s = normalize(pnResineSearch);   return !s || normalize(p).includes(s); });
  const filteredAmorceur = amorceurPns.filter((p) => { const s = normalize(pnAmorceurSearch); return !s || normalize(p).includes(s); });

  function handleCreateParcNumber() {
    if (!pnResine || !pnAmorceur) { alert("Merci de sélectionner un PN Résine et un PN Amorceur."); return; }
    const firstR = pnResine.split("_")[0].replace(/[^\p{L}\p{N}_-]+/gu, "_");
    const firstA = pnAmorceur.split("_")[0];
    const R = toFloat(qtyResineKg), A = toFloat(qtyAmorceurKg), T = toFloat(temperature), V = toFloat(viscosite);
    const d = new Date();
    const ds = d.getFullYear() + String(d.getMonth() + 1).padStart(2, "0") + String(d.getDate()).padStart(2, "0");
    setSolutionParcNumber(`SOL_${firstR}_${firstA}_R${R}_A${A}_T${T}_V${V}_${ds}`.replace(/[^\p{L}\p{N}_-]+/gu, "_"));
  }

  async function clientPrintFromApi(bodyForApi) {
    const win = window.open("", "_blank");
    if (win) { win.document.write(`<!doctype html><title>Préparation…</title><body>Préparation…</body>`); win.document.close(); }
    const res = await apiFetch("/api/labels/a4", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ ...bodyForApi, format: "PNG", download: false }) });
    if (!res.ok) { const err = await res.json().catch(() => ({})); throw new Error(err.message || `HTTP ${res.status}`); }
    const blob     = await res.blob();
    const blobUrl  = URL.createObjectURL(blob);
    const safePN   = String(bodyForApi.pn || "QR").replace(/[^\w\-]+/g, "_").slice(0, 64);
    const fileName = `A4_${bodyForApi.n || 1}x_${safePN}.png`;
    if (!win) { const a = document.createElement("a"); a.href = blobUrl; a.download = fileName; a.click(); URL.revokeObjectURL(blobUrl); return; }
    const AND = isAndroid();
    const html = `<!doctype html><html><head><meta charset="utf-8"><title>${fileName}</title>
    <style>html,body{height:100%;margin:0}
    #bar{position:fixed;inset:auto 0 0 0;background:#111;color:#fff;padding:8px;display:flex;gap:8px;align-items:center;z-index:10}
    #bar button,#bar a{background:#E6FF3E;color:#000;border:0;padding:8px 12px;font-weight:800;cursor:pointer;text-decoration:none}
    .v{position:fixed;left:0;right:0;top:0;bottom:44px;width:100%;object-fit:contain;background:#fff}
    </style></head><body>
    <img class="v" src="${blobUrl}" alt=""/>
    <div id="bar">
      ${AND ? "" : "<button id='btnPrint'>Imprimer</button>"}
      <a href="${blobUrl}" download="${fileName}">Télécharger</a>
      <button id="btnClose">Fermer</button>
    </div>
    <script>
      ${AND ? "" : "btnPrint.onclick=()=>{try{window.focus();window.print();}catch(e){}};"}
      btnClose.onclick=()=>{try{URL.revokeObjectURL('${blobUrl}');}catch(e){};setTimeout(()=>window.close(),100);};
      window.onafterprint=()=>{try{URL.revokeObjectURL('${blobUrl}');}catch(e){};setTimeout(()=>window.close(),200);};
      ${AND ? "" : "setTimeout(()=>{try{window.focus();window.print();}catch(e){}},400);"}
    </script></body></html>`;
    win.document.open(); win.document.write(html); win.document.close();
  }

  async function handlePrintSolution() {
    if (!solutionParcNumber) { alert("Merci de créer le Parc number solution d'abord."); return; }
    try {
      setPrinting(true);
      await clientPrintFromApi({ n: 2, pn: solutionParcNumber, detail: `Solution Résine-Amorceur ${pnResine} / ${pnAmorceur}`.trim(), date: new Date().toISOString().split("T")[0] });
      setSuccess("✅ Étiquette solution générée.");
    } catch (e) { setError(e.message || "Erreur impression"); }
    finally { setPrinting(false); }
  }

  function handleScanResine(results) {
    if (!results?.length) return;
    const pn = (results[0].rawValue || "").trim();
    if (!resinePns.includes(pn)) alert(`PN Résine "${pn}" non trouvé.`);
    else { setPnResine(pn); setPnResineSearch(pn); setShowResineList(false); }
    setShowResineScanner(false);
  }

  function handleScanAmorceur(results) {
    if (!results?.length) return;
    const pn = (results[0].rawValue || "").trim();
    if (!amorceurPns.includes(pn)) alert(`PN Amorceur "${pn}" non trouvé.`);
    else { setPnAmorceur(pn); setPnAmorceurSearch(pn); setShowAmorceurList(false); }
    setShowAmorceurScanner(false);
  }

  const msg = (key, params) => t(`prod.solutionResine.${key}`, params);
  const kg  = (v) => (Math.round(toFloat(v) * 100) / 100).toLocaleString("fr-FR");

  /** Traduit la réponse du serveur en consigne compréhensible pour l'opérateur. */
  function messageErreurServeur(status, serverMsg, qtyR, qtyA) {
    const m = String(serverMsg || "");
    if (status === 403) return msg("droits");
    if (status === 409) return msg("conflit");
    if (status === 404) return msg("lotIntrouvable", { pn: /amorceur/i.test(m) ? pnAmorceur : pnResine });
    if (status === 400) {
      const reste = (m.match(/([\d.]+)\s*kg restant/) || [])[1];
      if (/amorceur insuffisant/i.test(m)) return msg("stockAmorceurInsuffisant", { pn: pnAmorceur, reste: kg(reste), demande: kg(qtyA) });
      if (/stock insuffisant/i.test(m))    return msg("stockResineInsuffisant",   { pn: pnResine,   reste: kg(reste), demande: kg(qtyR) });
      if (/quantit/i.test(m))              return msg("qtyResineMissing");
      if (/manquant/i.test(m))             return msg("selectPn");
    }
    return msg("serveur", { detail: m || `HTTP ${status}` });
  }

  async function handleSubmit(e) {
    e.preventDefault(); setError(""); setSuccess("");
    if (!pnResine || !pnAmorceur) { setError(msg("selectPn")); return; }

    // Contrôles avant envoi, avec le stock affiché à l'écran
    const qtyR = toFloat(qtyResineKg);
    const qtyA = toFloat(qtyAmorceurKg);
    // toFloat transforme une saisie illisible en 0 : on la signale au lieu de l'envoyer
    const illisible = (v) => String(v ?? "").trim() !== "" && !Number.isFinite(Number(String(v).replace(",", ".")));
    if (illisible(qtyResineKg) || illisible(qtyAmorceurKg) || qtyR < 0 || qtyA < 0) { setError(msg("qtyInvalid")); return; }
    if (qtyR <= 0) { setError(msg("qtyResineMissing")); return; }
    const stR = resineStock[pnResine];
    if (stR && qtyR > stR.masseRestante + 1e-9) {
      setError(msg("stockResineInsuffisant", { pn: pnResine, reste: kg(stR.masseRestante), demande: kg(qtyR) }));
      return;
    }
    const stA = amorceurStock[pnAmorceur];
    if (stA && qtyA > stA.conditionnementRestant + 1e-9) {
      setError(msg("stockAmorceurInsuffisant", { pn: pnAmorceur, reste: kg(stA.conditionnementRestant), demande: kg(qtyA) }));
      return;
    }

    handleCreateParcNumber();
    const payload = {
      operator, entite, pnResine, pnAmorceur,
      qtyResineKg: toFloat(qtyResineKg), qtyAmorceurKg: toFloat(qtyAmorceurKg),
      qtyMelangeFinal: toFloat(qtyMelangeFinal), temperature: toFloat(temperature),
      viscosite: toFloat(viscosite), dureeRepos: Number(dureeRepos) || 0,
      solutionParcNumber, commentSolution, timestamp: new Date().toISOString(),
    };
    try {
      setSending(true);
      let res;
      try {
        res = await apiFetch("/api/production/resine-amorceur-solution", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
      } catch (netErr) {
        if (netErr?.status === 401) return; // session expirée : apiFetch renvoie vers la connexion
        setError("❌ " + msg("reseau"));
        return;
      }
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setError("❌ " + messageErreurServeur(res.status, d.message, qtyR, qtyA));
        // Le stock a pu bouger : on recharge pour que l'écran affiche le vrai restant
        if ([400, 404, 409].includes(res.status)) { loadResineStock(); loadAmorceurStock(); }
        return;
      }
      const result = await res.json().catch(() => ({}));
      if (result.masseRestanteKg != null && pnResine) {
        setResineStock((prev) => ({
          ...prev,
          [pnResine]: {
            ...(prev[pnResine] || {}),
            masseRestante: result.masseRestanteKg,
          },
        }));
      }
      if (result.conditionnementRestantKg != null && pnAmorceur) {
        setAmorceurStock((prev) => ({
          ...prev,
          [pnAmorceur]: {
            ...(prev[pnAmorceur] || {}),
            conditionnementRestant: result.conditionnementRestantKg,
          },
        }));
      }
      setQtyResineKg(""); setQtyAmorceurKg(""); setQtyMelangeFinal("");
      setTemperature(""); setViscosite(""); setDureeRepos(""); setCommentSolution("");
      const solPn = result.solutionPN || solutionParcNumber;
      setSuccess(
        result.masseRestanteKg != null && result.conditionnementRestantKg != null
          ? msg("succes", { pn: solPn, resine: kg(result.masseRestanteKg), amorceur: kg(result.conditionnementRestantKg) })
          : msg("succesSimple", { pn: solPn })
      );
    } catch (err) { setError("❌ " + msg("serveur", { detail: err.message || "?" })); }
    finally { setSending(false); }
  }

  const selectedResineStock = pnResine ? resineStock[pnResine] : null;
  const projectedResineRestante = selectedResineStock && qtyResineKg
    ? Math.max(selectedResineStock.masseRestante - toFloat(qtyResineKg), 0)
    : null;
  const selectedAmorceurStock = pnAmorceur ? amorceurStock[pnAmorceur] : null;
  const projectedAmorceurRestant = selectedAmorceurStock && qtyAmorceurKg
    ? Math.max(selectedAmorceurStock.conditionnementRestant - toFloat(qtyAmorceurKg), 0)
    : null;

  const timeStr = now.toLocaleTimeString("fr-FR");
  const dateStr = now.toLocaleDateString("fr-FR", { weekday: "short", day: "2-digit", month: "short", year: "numeric" });

  return (
    <div className="psra-page">
      <div className="psra-card">

        {/* HEADER */}
        <div className="psra-header">
          <span>Prélèvement &amp; Solution Liant &amp; Amorceur</span>
          <span className="psra-header-sub">Préparation de la solution résine + amorceur</span>
        </div>

        {/* NAV */}
        <div className="psra-nav">
          <button type="button" className="psra-btn-back" onClick={() => navigate("/menu")}>← Retour menu</button>
          <div className="psra-clock">
            <div className="psra-clock-time">{timeStr}</div>
            <div className="psra-clock-date">{dateStr}</div>
          </div>
        </div>

        {/* OPÉRATEUR */}
        <div className="psra-operator">
          <span className="psra-operator-dot" />
          {operator ? operator.toUpperCase() : "OPÉRATEUR NON CONNECTÉ"}
        </div>

        <form onSubmit={handleSubmit} className="psra-form">

          <div className="psra-section">
            <div className="psra-section-label">🏢 Entité qui prélève</div>
            <div className="psra-entite-row">
              {PRELEVEMENT_ENTITES.map((opt) => (
                <button
                  key={opt}
                  type="button"
                  className={`psra-entite-btn ${entite === opt ? "psra-entite-active" : ""}`}
                  onClick={() => setEntite(opt)}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>

          {/* PN RÉSINE + AMORCEUR */}
          <div className="psra-grid-2">

            {/* RÉSINE */}
            <div className="psra-section psra-section-resine">
              <div className="psra-section-label psra-label-resine">🧪 Liant (Résine)</div>
              <div className="psra-section-sublabel">Parc Number lot Liant</div>
              <div ref={resineBoxRef} style={{ position: "relative" }}>
                <div className="psra-select-row">
                  <input value={pnResineSearch}
                    onChange={(e) => { setPnResineSearch(e.target.value); setShowResineList(true); }}
                    onFocus={() => setShowResineList(true)}
                    placeholder="Sélectionner un PN"
                    className="psra-select-input" />
                  <button type="button" className="psra-scan-btn" onClick={() => setShowResineScanner(true)} title="Scanner QR Résine">📷</button>
                </div>
                {pnResine && (
                  <div className="psra-selected-badge">
                    <span>✔</span><strong>{pnResine}</strong>
                    <button type="button" className="psra-clear-btn" onClick={() => { setPnResine(""); setPnResineSearch(""); }}>✕</button>
                  </div>
                )}
                {showResineList && filteredResine.length > 0 && (
                  <div className="psra-dropdown" onMouseDown={(e) => e.preventDefault()}>
                    {filteredResine.map((p) => (
                      <div key={p} className="psra-dropdown-item"
                        onClick={() => { setPnResine(p); setPnResineSearch(p); setShowResineList(false); }}>{p}</div>
                    ))}
                  </div>
                )}
              </div>
              <div className="psra-field-sub">Quantité à prélever (kg)</div>
              <input type="text" inputMode="decimal" value={qtyResineKg}
                onChange={(e) => setQtyResineKg(e.target.value.replace(/[^0-9.,]/g, ""))}
                className="psra-num-input" placeholder="Ex: 20.5" />
              {selectedResineStock && (
                <div className="psra-stock-info">
                  <span>Masse initiale : <strong>{selectedResineStock.masse} kg</strong></span>
                  <span>Quantité restante : <strong>{selectedResineStock.masseRestante} kg</strong></span>
                  {projectedResineRestante != null && toFloat(qtyResineKg) > 0 && (
                    <span className="psra-stock-projected">
                      Après prélèvement : <strong>{projectedResineRestante} kg</strong>
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* AMORCEUR */}
            <div className="psra-section psra-section-amorceur">
              <div className="psra-section-label psra-label-amorceur">🧴 Amorceur</div>
              <div className="psra-section-sublabel">Parc Number lot Mélange Amorceur</div>
              <div ref={amorceurBoxRef} style={{ position: "relative" }}>
                <div className="psra-select-row">
                  <input value={pnAmorceurSearch}
                    onChange={(e) => { setPnAmorceurSearch(e.target.value); setShowAmorceurList(true); }}
                    onFocus={() => setShowAmorceurList(true)}
                    placeholder="Sélectionner un PN"
                    className="psra-select-input" />
                  <button type="button" className="psra-scan-btn" onClick={() => setShowAmorceurScanner(true)} title="Scanner QR Amorceur">📷</button>
                </div>
                {pnAmorceur && (
                  <div className="psra-selected-badge">
                    <span>✔</span><strong>{pnAmorceur}</strong>
                    <button type="button" className="psra-clear-btn" onClick={() => { setPnAmorceur(""); setPnAmorceurSearch(""); }}>✕</button>
                  </div>
                )}
                {showAmorceurList && filteredAmorceur.length > 0 && (
                  <div className="psra-dropdown" onMouseDown={(e) => e.preventDefault()}>
                    {filteredAmorceur.map((p) => (
                      <div key={p} className="psra-dropdown-item"
                        onClick={() => { setPnAmorceur(p); setPnAmorceurSearch(p); setShowAmorceurList(false); }}>{p}</div>
                    ))}
                  </div>
                )}
              </div>
              <div className="psra-field-sub">Quantité à prélever (kg)</div>
              <input type="text" inputMode="decimal" value={qtyAmorceurKg}
                onChange={(e) => setQtyAmorceurKg(e.target.value.replace(/[^0-9.,]/g, ""))}
                className="psra-num-input" placeholder="Ex: 5.0" />
              {selectedAmorceurStock && (
                <div className="psra-stock-info psra-stock-info-amorceur">
                  <span>Conditionnement initial : <strong>{selectedAmorceurStock.conditionnementInitial} kg</strong></span>
                  <span>Quantité restante : <strong>{selectedAmorceurStock.conditionnementRestant} kg</strong></span>
                  {projectedAmorceurRestant != null && toFloat(qtyAmorceurKg) > 0 && (
                    <span className="psra-stock-projected">
                      Après prélèvement : <strong>{projectedAmorceurRestant} kg</strong>
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* MÉLANGE FINAL */}
          <div className="psra-section psra-section-yellow">
            <div className="psra-section-label psra-label-yellow">⚖️ Quantité mélange finale (kg)</div>
            <input type="text" inputMode="decimal" value={qtyMelangeFinal}
              onChange={(e) => setQtyMelangeFinal(e.target.value.replace(/[^0-9.,]/g, ""))}
              className="psra-num-input psra-num-final" placeholder="Ex: 25.5" />
          </div>

          {/* PARAMÈTRES PROCESS */}
          <div className="psra-section">
            <div className="psra-section-label">⚙️ Paramètres process</div>
            <div className="psra-grid-3">
              <div className="psra-param-col">
                <div className="psra-param-label">Température (°C)</div>
                <input type="text" inputMode="decimal" value={temperature}
                  onChange={(e) => setTemperature(e.target.value.replace(/[^0-9.,]/g, ""))}
                  className="psra-num-input" placeholder="Ex: 22" />
              </div>
              <div className="psra-param-col">
                <div className="psra-param-label">Consistance / viscosité</div>
                <input type="text" value={viscosite}
                  onChange={(e) => setViscosite(e.target.value)}
                  className="psra-num-input" placeholder="Ex: 1200 cP" />
              </div>
              <div className="psra-param-col">
                <div className="psra-param-label">Durée de repos (min)</div>
                <input type="text" inputMode="numeric" value={dureeRepos}
                  onChange={(e) => setDureeRepos(e.target.value.replace(/[^0-9]/g, ""))}
                  className="psra-num-input" placeholder="Ex: 15" />
              </div>
            </div>
          </div>

          {/* PARC NUMBER SOLUTION */}
          <div className="psra-section psra-section-solution">
            <div className="psra-section-label psra-label-red">🏷 Parc Number Solution</div>
            <div className="psra-solution-row">
              <button type="button" onClick={handleCreateParcNumber} className="psra-btn-create-pn">
                ⚡ Créer Parc number solution &amp; Paramètres
              </button>
              <button type="button" onClick={handlePrintSolution}
                disabled={!solutionParcNumber || printing}
                className="psra-btn-print-pn">
                {printing ? "⏳" : "🖨 Print"}
              </button>
            </div>
            <input value={solutionParcNumber} readOnly
              placeholder="Parc number solution généré automatiquement…"
              className={`psra-pn-display ${solutionParcNumber ? "psra-pn-filled" : ""}`} />
            <div className="psra-field-sub psra-label-orange">💬 Commentaires / paramètres</div>
            <textarea rows={3} value={commentSolution}
              onChange={(e) => setCommentSolution(e.target.value)}
              className="psra-textarea"
              placeholder="Observations, paramètres complémentaires…" />
          </div>

          {/* MESSAGES */}
          {error   && <div className="psra-msg psra-msg-error">{error}</div>}
          {success && <div className="psra-msg psra-msg-success">{success}</div>}

          {/* SUBMIT */}
          <div className="psra-submit-row">
            <button type="submit" disabled={sending} className="psra-btn-submit">
              {sending ? "⏳ Envoi en cours…" : "✔ OK confirmation et envoi"}
            </button>
            <button type="button" className="psra-btn-time">
              Time
              <div className="psra-btn-time-sub">{timeStr}</div>
            </button>
          </div>

        </form>
      </div>

      {/* SCANNER RÉSINE */}
      {showResineScanner && (
        <div className="psra-modal-backdrop" onClick={() => setShowResineScanner(false)}>
          <div className="psra-modal-scanner" onClick={(e) => e.stopPropagation()}>
            <div className="psra-modal-title">📷 Scanner PN Résine (Liant)</div>
            <div style={{ borderRadius: 10, overflow: "hidden" }}>
              <Scanner onScan={handleScanResine} onError={console.error} constraints={{ facingMode: "environment" }} />
            </div>
            <button type="button" className="psra-btn-close-scanner" onClick={() => setShowResineScanner(false)}>✕ Fermer</button>
          </div>
        </div>
      )}

      {/* SCANNER AMORCEUR */}
      {showAmorceurScanner && (
        <div className="psra-modal-backdrop" onClick={() => setShowAmorceurScanner(false)}>
          <div className="psra-modal-scanner" onClick={(e) => e.stopPropagation()}>
            <div className="psra-modal-title">📷 Scanner PN Amorceur</div>
            <div style={{ borderRadius: 10, overflow: "hidden" }}>
              <Scanner onScan={handleScanAmorceur} onError={console.error} constraints={{ facingMode: "environment" }} />
            </div>
            <button type="button" className="psra-btn-close-scanner" onClick={() => setShowAmorceurScanner(false)}>✕ Fermer</button>
          </div>
        </div>
      )}

      <Style />
    </div>
  );
}

/* ── STYLES ── */
function Style() {
  return (
    <style>{`
      *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

      .psra-page {
        min-height: 100vh; width: 100%;
        background: radial-gradient(ellipse at top,#01283a 0%,#010F14 70%);
        display: flex; justify-content: center; align-items: flex-start;
        padding: 16px; font-family: 'Inter','Segoe UI',sans-serif;
      }
      .psra-card {
        width: 100%; max-width: 720px;
        background: #011e2b; border: 1px solid #1a4a5a;
        border-radius: 16px; overflow: hidden;
        box-shadow: 0 8px 40px rgba(0,0,0,0.6), 0 0 0 1px #0a3040;
      }

      /* HEADER */
      .psra-header {
        background: linear-gradient(135deg,#E6FF3E,#c8e800);
        color: #000; padding: 14px 20px;
        display: flex; flex-direction: column; align-items: center; gap: 2px;
      }
      .psra-header span   { font-size: 14px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.08em; text-align: center; }
      .psra-header-sub    { font-size: 11px; font-weight: 500; opacity: 0.6; }

      /* NAV */
      .psra-nav {
        background: #010f14; padding: 8px 16px;
        display: flex; justify-content: space-between; align-items: center;
        border-bottom: 1px solid #1a4a5a;
      }
      .psra-btn-back {
        background: transparent; color: #7ab8cc; border: 1px solid #1a4a5a;
        border-radius: 6px; padding: 5px 10px; font-size: 11px; font-weight: 600; cursor: pointer; transition: all 0.2s;
      }
      .psra-btn-back:hover { background: #1a4a5a; color: #fff; }
      .psra-clock          { text-align: right; }
      .psra-clock-time     { color: #E6FF3E; font-size: 15px; font-weight: 700; font-variant-numeric: tabular-nums; }
      .psra-clock-date     { color: #5a8a99; font-size: 10px; }

      /* OPÉRATEUR */
      .psra-operator {
        display: flex; align-items: center; gap: 10px;
        background: linear-gradient(90deg,#012836,#013d50);
        border: 1px solid #1a5a6a; border-radius: 10px;
        margin: 14px 20px 0; padding: 12px 16px;
        color: #fff; font-size: 14px; font-weight: 700; letter-spacing: 0.06em;
      }
      .psra-operator-dot { width: 10px; height: 10px; background: #00B050; border-radius: 50%; box-shadow: 0 0 6px #00B050; flex-shrink: 0; }

      /* FORM */
      .psra-form { padding: 16px 20px 24px; display: flex; flex-direction: column; gap: 14px; overflow-y: auto; flex: 1; }

      /* SECTIONS */
      .psra-section {
        background: #012030; border: 1px solid #1a4a5a;
        border-radius: 12px; padding: 14px 16px;
        display: flex; flex-direction: column; gap: 10px;
      }
      .psra-section-resine   { border-color: #00702F; background: #001a10; }
      .psra-section-amorceur { border-color: #8B0000; background: #1a0810; }
      .psra-section-yellow   { border-color: #FFD966; background: #1a1400; align-items: center; }
      .psra-section-solution { border-color: #C00000; background: #1a0808; }
      .psra-section-label    { font-size: 12px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: #7ab8cc; }
      .psra-entite-row { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; }
      .psra-entite-btn {
        background: #012030; color: #7ab8cc; border: 1px solid #1a4a5a;
        border-radius: 8px; padding: 10px 8px; font-size: 13px; font-weight: 700;
        cursor: pointer; transition: all 0.15s;
      }
      .psra-entite-btn:hover { background: #1a4a5a; color: #fff; }
      .psra-entite-active { background: #E6FF3E; color: #000; border-color: #E6FF3E; }
      .psra-section-sublabel { font-size: 11px; color: #7ab8cc; font-weight: 600; }
      .psra-label-resine     { color: #7aff9a; }
      .psra-label-amorceur   { color: #E5A5D5; }
      .psra-label-yellow     { color: #FFD966; }
      .psra-label-red        { color: #ff9090; }
      .psra-label-orange     { color: #FFB366; }
      .psra-field-sub        { font-size: 11px; font-weight: 600; color: #7ab8cc; }
      .psra-stock-info {
        display: flex; flex-direction: column; gap: 4px;
        margin-top: 4px; padding: 8px 10px;
        background: #002010; border: 1px solid #005020; border-radius: 8px;
        font-size: 11px; color: #aaffcc;
      }
      .psra-stock-info strong { color: #7aff9a; }
      .psra-stock-info-amorceur {
        background: #2a0f18; border-color: #6f213f; color: #ffd2ea;
      }
      .psra-stock-info-amorceur strong { color: #ffb6dc; }
      .psra-stock-projected { color: #FFD966; }

      /* GRIDS */
      .psra-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
      .psra-grid-3 { display: grid; grid-template-columns: repeat(3,1fr); gap: 10px; }

      /* SELECT ROW */
      .psra-select-row   { display: flex; gap: 8px; align-items: center; }
      .psra-select-input {
        flex: 1; background: #003818; color: #7aff9a;
        border: 1px solid #005020; border-radius: 8px;
        padding: 9px 12px; font-size: 13px; font-weight: 600;
        outline: none; transition: border 0.2s;
      }
      .psra-select-input:focus { border-color: #00B050; box-shadow: 0 0 0 2px rgba(0,176,80,0.15); }
      .psra-scan-btn {
        width: 40px; height: 40px; flex-shrink: 0; background: #012030;
        border: 1px solid #1a4a5a; border-radius: 8px;
        font-size: 18px; cursor: pointer; transition: background 0.2s;
      }
      .psra-scan-btn:hover { background: #1a4a5a; }
      .psra-selected-badge {
        display: flex; align-items: center; gap: 8px; margin-top: 6px;
        background: #002810; border: 1px solid #00B050; border-radius: 8px;
        padding: 5px 10px; font-size: 11px; color: #7aff9a;
      }
      .psra-selected-badge strong { flex: 1; font-size: 12px; word-break: break-all; }
      .psra-clear-btn { background: transparent; border: none; color: #ff8080; cursor: pointer; font-size: 14px; font-weight: 700; }
      .psra-dropdown {
        position: absolute; left: 0; right: 0; margin-top: 4px;
        max-height: 180px; overflow-y: auto;
        background: #002818; border: 1px solid #005020; border-radius: 8px; z-index: 50;
        box-shadow: 0 8px 24px rgba(0,0,0,0.5);
      }
      .psra-dropdown-item {
        padding: 7px 12px; color: #aaffcc; font-size: 12px;
        cursor: pointer; border-bottom: 1px solid #003818; transition: background 0.15s;
      }
      .psra-dropdown-item:hover { background: #003d20; }

      /* NUM INPUTS */
      .psra-num-input {
        width: 100%; background: #010f14; color: #fff;
        border: 1px solid #1a4a5a; border-radius: 8px;
        padding: 10px 12px; font-size: 18px; font-weight: 700; text-align: center;
        outline: none; transition: border 0.2s;
      }
      .psra-num-input:focus { border-color: #E6FF3E; box-shadow: 0 0 0 2px rgba(230,255,62,0.1); }
      .psra-num-final { font-size: 24px; max-width: 280px; background: #FFF2CC; color: #000; border-color: #FFD966; border-width: 2px; }
      .psra-num-final:focus { border-color: #C00000; }

      /* PARAMS */
      .psra-param-col   { display: flex; flex-direction: column; gap: 6px; }
      .psra-param-label {
        background: #F2F2F2; color: #000; border: 2px solid #C00000;
        border-radius: 8px; padding: 6px; text-align: center; font-weight: 700; font-size: 11px;
      }

      /* SOLUTION */
      .psra-solution-row  { display: flex; gap: 8px; align-items: center; }
      .psra-btn-create-pn {
        flex: 1; background: #C00000; color: #fff; border: 2px solid #800000;
        border-radius: 8px; padding: 10px 12px; font-weight: 700; font-size: 12px;
        cursor: pointer; transition: opacity 0.2s; text-align: center;
      }
      .psra-btn-create-pn:hover { opacity: 0.85; }
      .psra-btn-print-pn {
        background: #FFD966; color: #000; border: 2px solid #C00000;
        border-radius: 8px; padding: 10px 16px; font-weight: 800; font-size: 13px;
        cursor: pointer; transition: opacity 0.2s; white-space: nowrap;
      }
      .psra-btn-print-pn:disabled { opacity: 0.5; cursor: not-allowed; }
      .psra-btn-print-pn:hover:not(:disabled) { opacity: 0.85; }
      .psra-pn-display {
        width: 100%; background: #1a2a2a; color: #7ab8cc;
        border: 1px dashed #3a6a7a; border-radius: 8px;
        padding: 10px 14px; font-size: 11px; font-family: monospace;
        cursor: not-allowed; outline: none; letter-spacing: 0.03em; word-break: break-all;
      }
      .psra-pn-filled { background: #012836; color: #7aff9a; border-color: #00B050; border-style: solid; }
      .psra-textarea {
        width: 100%; background: #EAC1A8; color: #000;
        border: 2px solid #C00000; border-radius: 8px;
        padding: 10px 12px; font-size: 13px; resize: vertical; outline: none; line-height: 1.5;
        transition: border 0.2s;
      }
      .psra-textarea:focus { border-color: #900000; }

      /* MESSAGES */
      .psra-msg         { border-radius: 8px; padding: 10px 14px; font-size: 13px; font-weight: 600; }
      .psra-msg-error   { background: #2a0808; border: 1px solid #C00000; color: #ff9090; }
      .psra-msg-success { background: #002810; border: 1px solid #00B050; color: #80ff90; }

      /* SUBMIT */
      .psra-submit-row  { display: flex; gap: 10px; align-items: center; }
      .psra-btn-submit  {
        flex: 1; background: linear-gradient(135deg,#00B050,#008a3c);
        color: #000; border: none; border-radius: 10px;
        padding: 14px; font-size: 15px; font-weight: 800;
        text-transform: uppercase; letter-spacing: 0.08em; cursor: pointer;
        transition: opacity 0.2s, transform 0.1s;
        box-shadow: 0 4px 16px rgba(0,176,80,0.3);
      }
      .psra-btn-submit:hover:not(:disabled) { opacity: 0.9; transform: translateY(-1px); }
      .psra-btn-submit:disabled { opacity: 0.5; cursor: not-allowed; }
      .psra-btn-time {
        min-width: 110px; background: #C00000; color: #fff;
        border: 2px solid #7F0000; border-radius: 8px;
        padding: 8px 10px; font-weight: 700; font-size: 12px; cursor: default; text-align: center;
      }
      .psra-btn-time-sub { font-size: 11px; margin-top: 2px; font-variant-numeric: tabular-nums; }

      /* SCANNER MODAL */
      .psra-modal-backdrop {
        position: fixed; inset: 0; background: rgba(0,0,0,0.8);
        display: flex; justify-content: center; align-items: center;
        z-index: 9999; backdrop-filter: blur(4px);
      }
      .psra-modal-scanner {
        width: 90%; max-width: 420px; background: #011e2b;
        border: 1px solid #E6FF3E; border-radius: 14px; padding: 16px;
        display: flex; flex-direction: column; gap: 12px;
        box-shadow: 0 0 40px rgba(230,255,62,0.15);
      }
      .psra-modal-title { color: #E6FF3E; font-size: 14px; font-weight: 700; text-align: center; }
      .psra-btn-close-scanner {
        background: #C00000; color: #fff; border: none; border-radius: 8px;
        padding: 10px; font-weight: 700; font-size: 13px; cursor: pointer; transition: opacity 0.2s;
      }
      .psra-btn-close-scanner:hover { opacity: 0.85; }

      /* TABLETTE */
      @media (min-width: 768px) {
        .psra-card  { max-width: 860px; }
        .psra-header span { font-size: 17px; }
        .psra-form  { padding: 20px 28px 28px; gap: 18px; }
        .psra-select-input { font-size: 15px; padding: 11px 14px; }
        .psra-scan-btn     { width: 48px; height: 48px; font-size: 22px; }
        .psra-num-input    { font-size: 22px; padding: 12px; }
        .psra-num-final    { font-size: 28px; max-width: 340px; }
        .psra-param-label  { font-size: 12px; }
        .psra-btn-submit   { font-size: 16px; padding: 16px; }
      }

      /* DESKTOP */
      @media (min-width: 1024px) {
        .psra-card  { max-width: 1000px; }
        .psra-header span { font-size: 20px; }
        .psra-form  { padding: 20px 36px 36px; gap: 20px; }
        .psra-operator { margin: 16px 36px 0; font-size: 16px; }
        .psra-section  { padding: 18px 20px; border-radius: 14px; }
        .psra-section-label { font-size: 13px; }
        .psra-select-input  { font-size: 17px; padding: 12px 16px; }
        .psra-scan-btn      { width: 54px; height: 54px; font-size: 24px; }
        .psra-num-input     { font-size: 24px; padding: 14px; }
        .psra-num-final     { font-size: 32px; max-width: 400px; }
        .psra-param-label   { font-size: 13px; padding: 8px; }
        .psra-btn-create-pn { font-size: 14px; padding: 12px 16px; }
        .psra-btn-print-pn  { font-size: 14px; padding: 12px 20px; }
        .psra-pn-display    { font-size: 13px; padding: 12px 16px; }
        .psra-textarea      { font-size: 14px; padding: 12px 16px; }
        .psra-btn-submit    { font-size: 18px; padding: 18px; border-radius: 12px; }
        .psra-btn-time      { min-width: 140px; }
      }

      /* XXL */
      @media (min-width: 1400px) {
        .psra-page  { padding: 32px; }
        .psra-card  { max-width: 1280px; border-radius: 20px; }
        .psra-header { padding: 24px 48px; }
        .psra-header span { font-size: 24px; letter-spacing: 0.12em; }
        .psra-header-sub  { font-size: 14px; }
        .psra-nav   { padding: 14px 36px; }
        .psra-clock-time  { font-size: 22px; }
        .psra-operator    { margin: 18px 48px 0; font-size: 20px; padding: 16px 24px; border-radius: 14px; }
        .psra-operator-dot { width: 14px; height: 14px; }
        .psra-form  { padding: 24px 48px 40px; gap: 24px; }
        .psra-section { padding: 22px 24px; border-radius: 16px; }
        .psra-section-label  { font-size: 15px; letter-spacing: 0.1em; }
        .psra-section-sublabel { font-size: 13px; }
        .psra-grid-2        { gap: 18px; }
        .psra-grid-3        { gap: 14px; }
        .psra-select-input  { font-size: 20px; padding: 14px 18px; border-radius: 10px; }
        .psra-scan-btn      { width: 62px; height: 62px; font-size: 26px; border-radius: 10px; }
        .psra-dropdown-item { font-size: 14px; padding: 10px 16px; }
        .psra-num-input     { font-size: 28px; padding: 16px; border-radius: 10px; }
        .psra-num-final     { font-size: 40px; max-width: 480px; padding: 18px; border-radius: 12px; }
        .psra-param-label   { font-size: 14px; padding: 10px; border-radius: 10px; }
        .psra-solution-row  { gap: 12px; }
        .psra-btn-create-pn { font-size: 16px; padding: 14px 20px; border-radius: 10px; }
        .psra-btn-print-pn  { font-size: 16px; padding: 14px 24px; border-radius: 10px; }
        .psra-pn-display    { font-size: 14px; padding: 14px 18px; border-radius: 10px; }
        .psra-textarea      { font-size: 16px; padding: 14px 18px; border-radius: 10px; }
        .psra-msg           { font-size: 15px; padding: 14px 18px; border-radius: 10px; }
        .psra-btn-submit    { font-size: 22px; padding: 22px; border-radius: 14px; letter-spacing: 0.1em; }
        .psra-btn-time      { min-width: 170px; font-size: 14px; padding: 14px; border-radius: 10px; }
        .psra-btn-time-sub  { font-size: 13px; }
      }
    `}</style>
  );
}
